<template>
    <v-container class="py-7">
        <v-row>
            <v-col :cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span id="title" class="page-title">Modelos de documentos</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Início', disabled: false, to: '/home', exact: true },
                            { text: 'Modelos de documentos', disabled: true, exact: true },
                        ]"></v-breadcrumbs>
                    </div>
                    <div class="button-group">
                        <v-btn id="btnNew" type="button" color="primary" elevation="0" @click="openForm()">Incluir</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card class="table-card">
                    <v-card-title class="table-card-title">Modelos publicados no site</v-card-title>
                    <v-card-subtitle>
                        Os credores baixam estes arquivos em "Área do credor &gt; Modelos de documentos". A ordem define a
                        posição na página.
                    </v-card-subtitle>
                    <v-card-text>
                        <v-data-table :headers="headers" :items="items" :loading="loading" :disable-sort="true"
                            hide-default-footer :items-per-page="-1" no-data-text="Nenhum modelo cadastrado"
                            loading-text="Carregando dados..." class="app-table hover-table">
                            <template v-slot:[`item.updated_at`]="{ item }">
                                {{ item.updated_at | defaultDate("DD/MM/YYYY HH:mm") }}
                            </template>
                            <template v-slot:[`item.actions`]="{ item }">
                                <v-row align="center">
                                    <v-icon @click="openForm(item)" size="large" class="me-2">mdi-pencil-outline</v-icon>
                                    <v-icon @click="deleteItem(item)" size="large" class="me-2">mdi-delete</v-icon>
                                </v-row>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="form.show" max-width="640" persistent>
            <v-card>
                <v-card-title>{{ form.data.id ? 'Editar modelo' : 'Incluir modelo' }}</v-card-title>
                <v-card-text>
                    <v-form ref="form">
                        <v-text-field dense outlined label="Nome" v-model="form.data.name" :rules="[$rules.required]"
                            hide-details="auto" class="mb-4"></v-text-field>
                        <v-textarea dense outlined label="Descrição (opcional)" v-model="form.data.description" rows="2"
                            auto-grow counter="1000" hide-details="auto" class="mb-4"></v-textarea>
                        <v-text-field dense outlined label="Ordem" type="number" v-model="form.data.order"
                            hide-details="auto" class="mb-4"></v-text-field>
                        <v-file-input dense outlined v-model="form.file" prepend-icon="" prepend-inner-icon="mdi-paperclip"
                            :label="form.data.id ? 'Substituir arquivo (opcional)' : 'Arquivo'"
                            accept=".pdf,.doc,.docx,.odt,application/pdf" show-size hint="PDF, DOC, DOCX ou ODT, até 20 MB"
                            persistent-hint :rules="form.data.id ? [maxSize] : [$rules.required, maxSize]"></v-file-input>
                        <p v-if="form.data.file_name" class="mt-3 mb-0">
                            Arquivo atual: <a :href="form.data.file_url" target="_blank" rel="noopener">{{ form.data.file_name }}</a>
                        </p>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="form.show = false" :disabled="form.saving">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" @click="save" :loading="form.saving">Salvar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from "axios";

const MAX_SIZE_MB = 20;

export default {
    name: "DocumentTemplateList",

    data: () => ({
        loading: false,
        items: [],
        headers: [
            { text: "Ordem", value: "order", width: 90 },
            { text: "Nome", value: "name" },
            { text: "Arquivo", value: "file_name" },
            { text: "Atualizado em", value: "updated_at" },
            { text: "Ações", value: "actions", sortable: false },
        ],
        form: {
            show: false,
            saving: false,
            file: null,
            data: {},
        },
    }),

    created() {
        this.load();
    },

    methods: {
        maxSize(file) {
            return !file || file.size <= MAX_SIZE_MB * 1024 * 1024 || `O arquivo deve ter até ${MAX_SIZE_MB} MB`;
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get("/api/admin/document-templates");
                this.items = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        async openForm(item) {
            this.form.file = null;
            this.form.data = { order: this.items.length + 1 };

            if (item) {
                try {
                    const result = await axios.get(`/api/admin/document-templates/${item.id}`);
                    this.form.data = result.data.content;
                } catch (error) {
                    this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
                    return;
                }
            }

            this.form.show = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },

        async save() {
            if (!this.$refs.form.validate()) return;

            const formData = new FormData();
            formData.append("name", this.form.data.name);
            formData.append("description", this.form.data.description || "");
            formData.append("order", this.form.data.order || 0);
            if (this.form.file) formData.append("file", this.form.file);

            this.form.saving = true;
            try {
                const url = this.form.data.id
                    ? `/api/admin/document-templates/${this.form.data.id}`
                    : "/api/admin/document-templates";
                await axios.post(url, formData);

                this.$dialog.notify.info("Modelo salvo com sucesso", { position: "top-right", timeout: 2000 });
                this.form.show = false;
                this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.form.saving = false;
            }
        },

        async deleteItem(item) {
            const proceedAction = await this.$dialog.confirm({
                text: `Excluir o modelo "${item.name}"? Ele deixa de aparecer no site.`,
                title: "Excluir modelo",
                actions: { false: "Cancelar", true: "Excluir" },
            });

            if (!proceedAction) return;

            try {
                await axios.delete(`/api/admin/document-templates/${item.id}`);
                this.$dialog.notify.info("Modelo excluído", { position: "top-right", timeout: 2000 });
                this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            }
        },
    },
};
</script>
